import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Award, Briefcase, Trophy, Users, Network, GraduationCap, Rocket,
  Gift, ShieldCheck, ArrowRight, CheckCircle2, Star, TrendingUp, Send, Heart,
  Megaphone, BookOpen, Calendar, Target
} from "lucide-react";

const WHAT_WE_PROVIDE = [
  { i: Award, t: "Official Hostro Certificate", d: "Government-recognised ambassador certificate at the end of your tenure." },
  { i: Briefcase, t: "Internship Opportunities", d: "Top 10% performers get fast-tracked to Hostro full-time internships." },
  { i: TrendingUp, t: "Performance Incentives", d: "₹50 - ₹500 per converted referral. Top earners can take home ₹15K+/month." },
  { i: Gift, t: "Monthly Rewards & Swag", d: "Hostro merchandise, vouchers, exclusive drops, and surprise gifts." },
  { i: Trophy, t: "Leadership Experience", d: "Build campus chapters, host events, lead a team of mini-ambassadors." },
  { i: Network, t: "Networking", d: "Meet founders, VCs, fellow ambassadors from IITs, NITs and top colleges." },
  { i: Rocket, t: "Startup Ecosystem Exposure", d: "Behind-the-scenes access to Hostro Ventures' growth playbook." },
  { i: ShieldCheck, t: "Recommendation Letter", d: "Top performers get a signed LOR from the founders." },
];

const ROLES = [
  { i: Megaphone, t: "Promote Hostro on your campus" },
  { i: Users, t: "Refer students to PGs, hostels, co-living spaces" },
  { i: Calendar, t: "Conduct on-ground campus campaigns" },
  { i: BookOpen, t: "Create social media content about Hostro" },
  { i: Heart, t: "Collect student feedback & surveys" },
  { i: Target, t: "Organise Hostro events & meetups" },
  { i: Network, t: "Build campus communities and partnerships" },
  { i: TrendingUp, t: "Generate quality leads for Hostro" },
];

const WHO_CAN_APPLY = [
  "College Students", "University Students", "MBA Students",
  "Engineering Students", "Medical Students", "Coaching Students",
  "Anyone interested in marketing, sales, entrepreneurship, or startups",
];

const BENEFITS = [
  { i: TrendingUp, t: "Earn While Learning", d: "₹500 - ₹15,000/month based on performance.", c: "emerald" },
  { i: Trophy, t: "Build Leadership Skills", d: "Lead a chapter. Mentor juniors. Win awards.", c: "amber" },
  { i: Rocket, t: "Startup Experience", d: "Work hand-in-hand with the founding team.", c: "rose" },
  { i: Briefcase, t: "Internship Opportunities", d: "Top performers get PPI offers.", c: "blue" },
  { i: Network, t: "Networking", d: "Meet investors, founders, students from 50+ campuses.", c: "emerald" },
  { i: GraduationCap, t: "Career Growth", d: "LOR + Resume boost + Hostro alumni network.", c: "amber" },
];

export default function StudentAmbassador() {
  const [form, setForm] = useState({
    full_name: "", email: "", mobile: "", college: "", city: "",
    course: "", year: "", linkedin: "", instagram: "",
    why_join: "", reach_size: "", leadership_experience: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/ambassador/apply", form);
      setDone(data);
      toast.success("Application submitted! 🎉");
      setForm({
        full_name: "", email: "", mobile: "", college: "", city: "",
        course: "", year: "", linkedin: "", instagram: "",
        why_join: "", reach_size: "", leadership_experience: "",
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed — please try again.");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:border-[#34C97B] focus:ring-2 focus:ring-[#E8FFF3] outline-none transition text-sm";
  const labelCls = "text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block";
  const benefitColor = (c) => ({
    emerald: "bg-[#E8FFF3] text-[#22B36A] border-[#34C97B]/30",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
  })[c] || "bg-gray-50 text-gray-700 border-gray-200";

  return (
    <div className="bg-white min-h-screen" data-testid="ambassador-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-gray-200">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E8FFF3] via-white to-amber-50/40 pointer-events-none"/>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#34C97B]/10 rounded-full blur-3xl"/>
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-16 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#34C97B] text-white text-xs font-semibold uppercase tracking-wider">
              <Sparkles size={14}/> Hostro Student Ambassador Program
            </div>
            <h1 className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl mt-5 text-[#111111]">
              Become a Hostro<br/>
              <span className="text-[#34C97B]">Student Ambassador</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-gray-600 max-w-2xl">
              Lead your campus, earn rewards, build your professional profile, and help students find better accommodation through Hostro.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#apply" className="inline-flex items-center gap-2 bg-[#34C97B] hover:bg-[#22B36A] text-white font-semibold px-6 py-3.5 rounded-xl transition" data-testid="cta-apply">
                Apply Now <ArrowRight size={16}/>
              </a>
              <a href="#about" className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-[#111111] font-semibold px-6 py-3.5 rounded-xl transition" data-testid="cta-learn-more">
                Learn More
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 flex-wrap text-sm">
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#34C97B]"/> Certificate</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#34C97B]"/> Internship potential</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#34C97B]"/> Cash rewards</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#34C97B]"/> LOR for top performers</div>
            </div>
          </div>

          {/* Visual */}
          <div className="relative" aria-hidden="true">
            <div className="bg-gradient-to-br from-[#34C97B] to-[#22B36A] rounded-3xl p-8 text-white shadow-2xl rotate-2">
              <Award size={48} className="text-amber-300"/>
              <div className="mt-4 text-xs font-semibold uppercase tracking-wider opacity-80">Certificate of Recognition</div>
              <div className="font-display font-black text-3xl mt-2 leading-tight">Hostro Campus<br/>Ambassador 2026</div>
              <div className="mt-6 pt-4 border-t border-white/20 text-xs opacity-80">Awarded for outstanding contribution to student welfare and campus growth.</div>
            </div>
            <div className="absolute -top-4 -left-4 bg-white rounded-2xl shadow-xl p-4 border border-gray-200 -rotate-3">
              <Trophy size={20} className="text-amber-500"/>
              <div className="font-bold text-sm mt-1 text-[#111111]">Top Ambassador</div>
              <div className="font-display font-black text-2xl text-[#34C97B]">₹15K+</div>
              <div className="text-xs text-gray-500">monthly earnings</div>
            </div>
            <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl p-4 border border-gray-200 rotate-2">
              <Users size={20} className="text-[#34C97B]"/>
              <div className="font-bold text-sm mt-1 text-[#111111]">200+</div>
              <div className="text-xs text-gray-500">ambassadors across India</div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        <div className="max-w-3xl">
          <span className="px-3 py-1 rounded-full bg-[#E8FFF3] text-[#22B36A] text-xs font-semibold uppercase tracking-wider border border-[#34C97B]/30">About the Program</span>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-4 tracking-tight text-[#111111]">
            Real startup experience.<br/>
            Real campus impact.
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Hostro Student Ambassador Program is designed for students who want to gain hands-on startup experience while representing India&apos;s smartest student-living platform on their campus.
          </p>
        </div>
      </section>

      {/* WHAT WE PROVIDE */}
      <section className="bg-gradient-to-b from-white to-[#E8FFF3]/40 border-y border-gray-200 py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <h2 className="font-display font-black text-3xl md:text-4xl text-[#111111]">What we provide</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {WHAT_WE_PROVIDE.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-[#34C97B] hover:shadow-xl hover:-translate-y-1 transition" data-testid={`provide-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#E8FFF3] border border-[#34C97B]/30 grid place-items-center text-[#22B36A]">
                  <b.i size={20}/>
                </div>
                <h3 className="font-display font-black text-lg mt-3 text-[#111111]">{b.t}</h3>
                <p className="mt-1 text-sm text-gray-600 leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROLES & RESPONSIBILITIES */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        <div className="max-w-3xl">
          <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-wider">Roles & Responsibilities</span>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-4 tracking-tight text-[#111111]">What you&apos;ll do</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
          {ROLES.map((r, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 flex items-start gap-3" data-testid={`role-${i}`}>
              <div className="w-9 h-9 rounded-lg bg-[#E8FFF3] grid place-items-center text-[#22B36A] flex-shrink-0">
                <r.i size={18}/>
              </div>
              <div className="font-semibold text-sm text-[#111111] leading-snug">{r.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHO CAN APPLY */}
      <section className="bg-[#111111] text-white py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <h2 className="font-display font-black text-4xl md:text-5xl text-white">Who can apply?</h2>
          <p className="mt-2 text-white/70">If you&apos;re a student in India who loves marketing, sales, entrepreneurship or startups — we want you.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
            {WHO_CAN_APPLY.map((w, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur flex items-center gap-2" data-testid={`who-${i}`}>
                <CheckCircle2 size={18} className="text-[#34C97B] flex-shrink-0"/>
                <span className="text-sm font-semibold text-white">{w}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20">
        <div className="max-w-2xl">
          <span className="px-3 py-1 rounded-full bg-[#E8FFF3] text-[#22B36A] text-xs font-semibold uppercase tracking-wider border border-[#34C97B]/30">Why join</span>
          <h2 className="font-display font-black text-4xl md:text-5xl mt-3 tracking-tight text-[#111111]">Benefits that compound.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {BENEFITS.map((b, i) => (
            <div key={i} className="bg-white rounded-3xl border border-gray-200 p-6 hover:shadow-xl hover:-translate-y-1 transition" data-testid={`benefit-${i}`}>
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl border ${benefitColor(b.c)}`}>
                <b.i size={22}/>
              </div>
              <h3 className="font-display font-black text-xl mt-4 text-[#111111]">{b.t}</h3>
              <p className="mt-2 text-gray-600 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="bg-gradient-to-b from-[#E8FFF3]/40 to-white border-t border-gray-200 py-20">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <div className="text-center">
            <span className="px-3 py-1 rounded-full bg-[#34C97B] text-white text-xs font-semibold uppercase tracking-wider">Apply Now</span>
            <h2 className="font-display font-black text-4xl md:text-5xl mt-4 tracking-tight text-[#111111]">
              Ready to lead?<br/>
              <span className="text-[#34C97B]">Apply in 2 minutes.</span>
            </h2>
          </div>

          {done ? (
            <div className="mt-12 bg-white rounded-3xl border-2 border-[#34C97B]/30 p-10 text-center shadow-xl" data-testid="ambassador-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#E8FFF3] border-2 border-[#34C97B] grid place-items-center">
                <CheckCircle2 size={32} className="text-[#22B36A]"/>
              </div>
              <h3 className="font-display font-black text-3xl mt-5 text-[#111111]">You&apos;re in! 🎉</h3>
              <p className="text-gray-600 mt-3">
                Thank you for applying to the Hostro Student Ambassador Program. Our team will review your application and contact you within 48 hours.
              </p>
              <div className="mt-5 inline-block bg-[#E8FFF3] border border-[#34C97B]/30 rounded-xl px-5 py-3">
                <div className="text-xs font-bold uppercase tracking-wider text-[#22B36A]">Your tentative referral code</div>
                <div className="font-display font-black text-2xl text-[#22B36A] mt-1">{done.referral_code}</div>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                <Link to="/" className="inline-flex items-center justify-center gap-2 bg-[#34C97B] hover:bg-[#22B36A] text-white font-semibold px-5 py-3 rounded-xl transition">Back home <ArrowRight size={16}/></Link>
                <button onClick={()=>setDone(null)} className="inline-flex items-center justify-center gap-2 bg-white border border-gray-200 hover:bg-gray-50 text-[#111111] font-semibold px-5 py-3 rounded-xl transition">Submit another</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-12 bg-white rounded-3xl border border-gray-200 p-6 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-5" data-testid="ambassador-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Full name *</label>
                  <input required className={inputCls} value={form.full_name} onChange={e=>set("full_name", e.target.value)} data-testid="amb-name"/>
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <input required type="email" className={inputCls} value={form.email} onChange={e=>set("email", e.target.value)} data-testid="amb-email"/>
                </div>
                <div>
                  <label className={labelCls}>Mobile *</label>
                  <input required className={inputCls} value={form.mobile} onChange={e=>set("mobile", e.target.value)} placeholder="+91 9876543210" data-testid="amb-mobile"/>
                </div>
                <div>
                  <label className={labelCls}>City *</label>
                  <input required className={inputCls} value={form.city} onChange={e=>set("city", e.target.value)} placeholder="Jaipur, Delhi..." data-testid="amb-city"/>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>College / University *</label>
                  <input required className={inputCls} value={form.college} onChange={e=>set("college", e.target.value)} placeholder="Poornima University, IIT Delhi..." data-testid="amb-college"/>
                </div>
                <div>
                  <label className={labelCls}>Course *</label>
                  <input required className={inputCls} value={form.course} onChange={e=>set("course", e.target.value)} placeholder="B.Tech, MBA, B.A..." data-testid="amb-course"/>
                </div>
                <div>
                  <label className={labelCls}>Year of study *</label>
                  <select required className={inputCls} value={form.year} onChange={e=>set("year", e.target.value)} data-testid="amb-year">
                    <option value="">Select...</option>
                    <option>1st year</option>
                    <option>2nd year</option>
                    <option>3rd year</option>
                    <option>4th year</option>
                    <option>5th year+</option>
                    <option>Coaching student</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>LinkedIn profile</label>
                  <input className={inputCls} value={form.linkedin} onChange={e=>set("linkedin", e.target.value)} placeholder="https://linkedin.com/in/..." data-testid="amb-linkedin"/>
                </div>
                <div>
                  <label className={labelCls}>Instagram profile</label>
                  <input className={inputCls} value={form.instagram} onChange={e=>set("instagram", e.target.value)} placeholder="@your_handle" data-testid="amb-instagram"/>
                </div>
              </div>
              <div>
                <label className={labelCls}>Why do you want to become a Hostro Ambassador? *</label>
                <textarea required className={`${inputCls} min-h-[120px]`} value={form.why_join} onChange={e=>set("why_join", e.target.value)} placeholder="Share your motivation, goals, and what excites you about Hostro..." data-testid="amb-why"/>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>How many students can you reach on campus?</label>
                  <input className={inputCls} value={form.reach_size} onChange={e=>set("reach_size", e.target.value)} placeholder="e.g. 200, 500+, 1000" data-testid="amb-reach"/>
                </div>
                <div>
                  <label className={labelCls}>Previous leadership experience (optional)</label>
                  <input className={inputCls} value={form.leadership_experience} onChange={e=>set("leadership_experience", e.target.value)} placeholder="Club president, event lead..." data-testid="amb-experience"/>
                </div>
              </div>
              <button disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#34C97B] hover:bg-[#22B36A] disabled:opacity-60 text-white font-semibold py-4 rounded-xl text-lg transition" data-testid="amb-submit">
                {busy ? "Submitting..." : <>Submit application <Send size={16}/></>}
              </button>
              <p className="text-xs text-gray-500 text-center">By applying, you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.</p>
            </form>
          )}
        </div>
      </section>

      {/* FAQ-lite */}
      <section className="max-w-3xl mx-auto px-5 md:px-10 py-16">
        <h3 className="font-display font-black text-2xl text-center text-[#111111]">Questions?</h3>
        <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <strong className="text-[#111111]">Is there a fee?</strong>
            <p className="text-gray-600 mt-1">No. It&apos;s 100% free. We pay you.</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <strong className="text-[#111111]">How long is the program?</strong>
            <p className="text-gray-600 mt-1">3 to 6 months. Renewable based on performance.</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <strong className="text-[#111111]">Will it affect studies?</strong>
            <p className="text-gray-600 mt-1">2-4 hours/week is enough. Fully flexible.</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <strong className="text-[#111111]">When will I get paid?</strong>
            <p className="text-gray-600 mt-1">Monthly payouts on the 5th. UPI/bank transfer.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
